<section class="container">
    <div class="header-wrapper">
        <h1>Feature Flags</h1>
    </div>
    <div class="content-wrapper">
        @if (error.length > 0) {
            <ul class="error-list">
                @for (e of error; track e) {
                    <li>
                        <pre>{{e | json}}</pre>
                    </li>
                }
            </ul>
        }
        <div class="intro">
            <p>
                This is where to manage feature flags for a tenant. Please <strong>do not</strong> enable features for live
                tenants unless you are confident they are ready and have been advised to by the Product Team. 
                Some of these features may not be complete and enabling them could <strong>expose tenants to untested, 
                unstable or unsecure software</strong>.
            </p>
            <p>
                Some features can be enabled per module - you can leave the module filter empty to enable the feature for
                all modules.
            </p>
        </div>
        <mat-form-field appearance="outline" class="search-filter">
            <mat-label>Search feature flags</mat-label>
            <input data-qa-id="feature-flags-input-filter"
                   [(ngModel)]="flagsFilter"
                   (ngModelChange)="refreshVisibleFlags()"
                   [name]="'flagsFilter'"
                   [placeholder]="'Search'"
                   matInput
                   type="text"/>
        </mat-form-field>
        <div class="info">
            <p>
                Some mobile related feature flags will require the mobile related caches to be cleared on a feature flag change.
                Click here to clear those caches:
            </p>
            <p>
                <a data-qa-id="feature-flags-btn-clear-mobile-cache" mat-raised-button class="mat-primary" [disabled]="!hasAccess()" (click)="clearMobileCache()">
                    Clear Mobile Caches
                </a>
            </p>
        </div>
        @if (hasAccess()) {
            <div class="add-flag">
                <p class="title">Add a feature flag to this tenant</p>
                <p>
                    This method is intended to be used in situations where you might need to enable a hidden feature flag on a specific tenant,
                    instead of it being rolled out to all environments on deployment. Be very careful when adding and ensure the flag name is
                    exactly the right spelling and casing. Adding a flag this way will add it turned ON and with no module filter.
                </p>
                <input data-qa-id="feature-flags-input-new-flag-name" matInput type="text" placeholder="Name" [(ngModel)]="newFeatureFlagName" name="new-feature-flag-name" />
                <a data-qa-id="feature-flags-btn-add-flag" mat-raised-button class="mat-primary tableBtn" [disabled]="!newFeatureFlagName" (click)="addFlag()">
                    <mat-icon>save</mat-icon>
                </a>
            </div>
        }

        @if (loading) {
            <div class="loading">
                Loading Feature Flags...
            </div>
        }
        @if (!loading && selectedGroup) {
            <div class="table-container">
                <table data-qa-id="feature-flags-table-flags" class="mdl-data-table">
                    <thead>
                        @if (groups.length > 0) {
                            <tr class="group-filter">
                                <th colspan="2">
                                    <span>Filter by Feature Group: </span>
                                    <mat-select data-qa-id="feature-flags-select-group-filter" [(value)]="selectedGroup" (selectionChange)="groupChanged()">
                                        @for (group of groups; track group) {
                                            <mat-option [value]="group">{{translationService.getTranslation(group.LabelAlias, 'Text') | async}}</mat-option>
                                        }
                                    </mat-select>
                                    @if (selectedGroup.Name !== 'All') {
                                        <span class="select-all-label">Toggle the whole group:</span>
                                    }
                                </th>
                                <th class="enabled">
                                    @if (selectedGroup.Name !== 'All') {
                                        <div>
                                            <mat-checkbox [(ngModel)]="groupToggle" (change)="groupToggleChanged()"> </mat-checkbox>
                                        </div>
                                    }
                                </th>
                                <th class="submit">
                                    @if (selectedGroup.Name !== 'All' && groupToggleCanSave) {
                                        <div>
                                            <a mat-raised-button class="mat-primary tableBtn save"
                                               matTooltip="Save the changes"
                                               [disabled]="!hasAccess()"
                                               (click)="saveGroupToggle()">
                                                <mat-icon>save</mat-icon>
                                            </a>
                                            <a mat-raised-button class="mat-basic tableBtn clear"
                                               matTooltip="Undo the changes"
                                               (click)="resetGroupToggle()">
                                                <mat-icon>undo</mat-icon>
                                            </a>
                                        </div>
                                    }
                                </th>
                            </tr>
                        }
                        <tr>
                            <th>Name</th>
                            <th>Module Filters</th>
                            <th class="enabled">Enabled</th>
                            <th><!-- save/undo buttons --></th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (featureFlag of visibleFlags; track featureFlag.Name) {
                            <tr class="translation-row">
                                <td class="name">
                                    <div class="name-row">
                                        <div class="name-wrapper">
                                            <div class="node-name">
                                                &quot;{{featureFlag.Name}}&quot;
                                            </div>
                                            <div class="label">
                                                {{(translationService.getTranslation(featureFlag.LabelAlias, 'Text') | async)}}
                                            </div>
                                        </div>
                                        @if (getDescription(featureFlag); as description) {
                                            <mat-icon class="description-icon"
                                                      data-qa-id="feature-flags-icon-description"
                                                      tabindex="0"
                                                      role="img"
                                                      aria-hidden="false"
                                                      [attr.aria-label]="'About ' + featureFlag.Name"
                                                      [matTooltip]="description"
                                                      matTooltipPosition="right"
                                                      matTooltipClass="feature-flag-description-tooltip">info_outline</mat-icon>
                                        }
                                    </div>
                                    @for (group of featureFlag.FeatureGroups; track group) {
                                        <div class="groups">
                                            {{translationService.getTranslation(group.LabelAlias, 'Text') | async}}
                                        </div>
                                    }
                                </td>
                                <td class="module-filters">
                                    @if (!featureFlag.GlobalFlag) {
                                        <div class="module-select">
                                            <mat-select multiple placeholder="Applies to all modules" [(value)]="featureFlag.ModulesEnabledFor">
                                                @for (module of modules; track module.id) {
                                                    <mat-option [value]="module.id">{{module.text}}</mat-option>
                                                }
                                            </mat-select>
                                        </div>
                                    } @else {
                                        <div class="no-module-select">
                                            <mat-icon>select_all</mat-icon>
                                        </div>
                                    }
                                </td>
                                <td class="enabled">
                                    <mat-checkbox [(ngModel)]="featureFlag.Enabled"> </mat-checkbox>
                                </td>
                                <td class="submit">
                                    <div [style.visibility]="hasModification(featureFlag) ? 'visible' : 'hidden'">
                                        <a mat-raised-button class="mat-primary tableBtn save"
                                           matTooltip="Save the changes"
                                           [disabled]="!hasAccess()"
                                           (click)="saveFlag(featureFlag)">
                                            <mat-icon>save</mat-icon>
                                        </a>
                                        <a mat-raised-button class="mat-basic tableBtn clear"
                                           matTooltip="Undo the changes"
                                           (click)="resetFlag(featureFlag)">
                                            <mat-icon>undo</mat-icon>
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        }
    </div>
</section>
